import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  useConfirmPasswordResetMutation,
  useRequestPasswordResetMutation,
} from '../../store/api/localApi';
import { PASSWORD_RULES, isPasswordValid } from '../../utils/password';

// How long to wait before surfacing the "didn't get a code?" hint.
// Long enough that real email delivery has plausibly happened; short
// enough that a user with a typo isn't stuck for ages wondering.
const HINT_AFTER_MS = 20_000;

const getErrorMessage = (error: unknown): string => {
  const fallback = 'Could not reset password. Please try again.';
  if (!error || typeof error !== 'object') return fallback;
  const e = error as { data?: any; message?: unknown };
  if (typeof e.data === 'string') return e.data;
  if (e.data && typeof e.data === 'object') {
    if (typeof e.data.message === 'string') return e.data.message;
    if (typeof e.data.error === 'string') return e.data.error;
  }
  if (typeof e.message === 'string') return e.message;
  return fallback;
};

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Email is carried over via query string from /forgot-password. If a parent
  // bookmarks /reset-password directly, they can still edit it here.
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resentNotice, setResentNotice] = useState<string | null>(null);

  const [confirmReset, { isLoading: confirming }] = useConfirmPasswordResetMutation();
  const [requestReset, { isLoading: resending }] = useRequestPasswordResetMutation();

  // Surface a soft hint after a delay if the user hasn't entered the code yet.
  // The "no code arrived" case is indistinguishable from "user typed a wrong
  // email" by design (we don't leak account existence), so we nudge the user
  // to double-check the address rather than letting them stew silently.
  const [showHint, setShowHint] = useState(false);
  useEffect(() => {
    if (code.length > 0) {
      setShowHint(false);
      return;
    }
    const id = window.setTimeout(() => setShowHint(true), HINT_AFTER_MS);
    return () => window.clearTimeout(id);
  }, [code]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResentNotice(null);
    try {
      await confirmReset({ email, code, newPassword }).unwrap();
      // Redirect to login with a hint so the page can offer "you can sign in
      // with your new password" copy if it wants to.
      navigate('/login?reset=1');
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleResend = async () => {
    setError(null);
    setResentNotice(null);
    if (!email) {
      setError('Enter your email first.');
      return;
    }
    try {
      await requestReset({ email }).unwrap();
      setResentNotice('A new code has been sent to your email.');
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Enter your reset code
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            If your email is registered, we've sent a 6-digit code. Enter it below along with a new
            password.
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div
              data-testid="reset-password-error"
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded"
            >
              {error}
            </div>
          )}
          {resentNotice && (
            <div
              data-testid="reset-password-resent"
              className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded"
            >
              {resentNotice}
            </div>
          )}
          {showHint && !resentNotice && !error && (
            // Soft prompt for the silent dead-end case (typo'd email, or user
            // never registered). We can't tell them "your email isn't on file"
            // without leaking account existence, so we nudge them to verify
            // the address and offer a quick path to resend or go back.
            <div
              data-testid="reset-password-hint"
              className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded text-sm space-y-1"
            >
              <p className="font-medium">Didn't get a code?</p>
              <p>
                Check your spam folder, or double-check the email above is the one you signed up
                with. You can{' '}
                <button
                  type="button"
                  onClick={handleResend}
                  className="underline font-medium hover:text-blue-900"
                >
                  resend the code
                </button>{' '}
                or{' '}
                <Link to="/forgot-password" className="underline font-medium hover:text-blue-900">
                  use a different email
                </Link>
                .
              </p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                data-testid="reset-password-email"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
              />
            </div>
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                Reset code
              </label>
              <input
                id="code"
                name="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                required
                data-testid="reset-password-code"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm tracking-widest"
                placeholder="123456"
                value={code}
                onChange={(ev) => setCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </div>
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                New password
              </label>
              <div className="mt-1 relative">
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  data-testid="reset-password-new"
                  className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Choose a strong password"
                  value={newPassword}
                  onChange={(ev) => setNewPassword(ev.target.value)}
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="reset-password-toggle"
                  className="absolute inset-y-0 right-0 z-20 pr-3 flex items-center"
                  onClick={() => setShowPassword((c) => !c)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              <ul data-testid="reset-password-requirements" className="mt-2 space-y-1 text-xs">
                {PASSWORD_RULES.map((rule) => {
                  const passed = rule.test(newPassword);
                  return (
                    <li
                      key={rule.id}
                      data-testid={`reset-password-rule-${rule.id}`}
                      data-passed={passed ? 'true' : 'false'}
                      className={`flex items-center gap-1.5 ${
                        passed ? 'text-green-700' : 'text-gray-500'
                      }`}
                    >
                      {passed ? (
                        <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      ) : (
                        <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      )}
                      {rule.label}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <button
            type="submit"
            disabled={confirming || !email || code.length !== 6 || !isPasswordValid(newPassword)}
            data-testid="reset-password-submit"
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {confirming ? 'Saving…' : 'Set new password'}
          </button>

          <div className="flex items-center justify-between text-sm">
            <Link
              to="/login"
              data-testid="reset-password-back"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Back to sign in
            </Link>
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              data-testid="reset-password-resend"
              className="font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              {resending ? 'Sending…' : 'Resend code'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
